import ChecklistItem from "models/checklist/checklist-item/checklist-item";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { ProcessingError } from "store/slices/app-slice";
import { useAppStore } from "store/store";

import AnalyticsService from "services/analytics/analytics";
import useMutatedPagePath from "./useMutatedPagePath";
import usePaxSession from "../usePaxSession";
import { getGeneralAnalyticsParams } from "./useGeneralAnalytics";
import { getPaxLevelParams } from "./usePaxLevelAnalytics";
import {
  ApplyDocumentErrorIdMismatch,
  ApplyDocumentErrorRecognitionFailed,
} from "services/data-provider/ApplyDocumentResponse";

export const typeOfProcessingError = (
  error: ProcessingError
): "id_mismatch" | "upload_error" | "other" => {
  if (error instanceof ApplyDocumentErrorIdMismatch) {
    return "id_mismatch";
  }

  if (error instanceof ApplyDocumentErrorRecognitionFailed) {
    return "upload_error";
  }

  return "other";
};

export const getItemLevelParams = (
  item?: ChecklistItem | null,
  itemType?: string | null
): { [key: string]: any } => {
  if (!item && !itemType) {
    return {};
  }
  const processingError = useAppStore.getState().processingError;

  const options: { [key: string]: any } = {
    item_type: item ? item.itemType : itemType,
    item_status: item ? item.status : "unknown",
    upload_error_shown: Number(
      processingError &&
        typeOfProcessingError(processingError) === "upload_error"
    ),
    id_match_error_shown: Number(
      processingError &&
        typeOfProcessingError(processingError) === "id_mismatch"
    ),
  };

  return options;
};

const useItemLevelAnalytics = () => {
  const [path, title, location] = useMutatedPagePath();
  const locale = useAppStore((state) => state.locale);
  const paxSessions = useAppStore((state) => state.paxSessions);
  const paxSession = usePaxSession();
  const journey = useAppStore((state) => state.journey);

  const { itemId, itemType } = useParams<{
    itemId: string;
    itemType: string;
  }>();
  const item = paxSession?.checklist.getItemById(itemId);

  useEffect(() => {
    AnalyticsService.shared.trackEvent(
      "page_view",
      {
        ...getGeneralAnalyticsParams(
          paxSessions,
          journey,
          path,
          title,
          location,
          locale
        ),
        ...getPaxLevelParams(paxSessions, paxSession?.paxSessionId || null),
        ...getItemLevelParams(item, itemType),
      },
      false // isScanRelated
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useItemLevelAnalytics;
